<template>
  <v-navigation-drawer
    :value="value"
    right
    temporary
    fixed
    width="848"
    role="dialog"
    aria-modal="true"
    aria-labelledby="gg-drawer-createplaylist-title"
    class="gg-idrawer"
    @input="$emit('input', $event)"
  >
    <div class="gg-idrawer__header">
      <h2 id="gg-drawer-createplaylist-title" class="gg-idrawer__title">Criar playlist</h2>
      <button type="button" class="gg-idrawer__close" aria-label="Fechar" @click="close">
        <v-icon size="24">mdi-close</v-icon>
      </button>
    </div>

    <div class="gg-idrawer__body" @scroll="onScroll">
      <label class="gg-create-pl__label" for="gg-new-playlist-name">
        Nome da playlist<span class="gg-create-pl__required" aria-hidden="true">*</span>
      </label>
      <input
        id="gg-new-playlist-name"
        v-model="nome"
        type="text"
        class="gg-create-pl__name"
        placeholder="Dê um nome à playlist"
        required
      />

      <hr class="gg-create-pl__rule" />

      <GloboGraphicsTemplatePicker ref="picker" :selected.sync="selected" />
    </div>

    <div class="gg-idrawer__footer">
      <button type="button" class="gg-idrawer__submit" :disabled="!canSave" @click="save">
        {{ saving ? 'Salvando…' : 'Salvar' }}
      </button>
    </div>
  </v-navigation-drawer>
</template>

<script>
/**
 * Creates a playlist and seeds it from the Vizrt catalog in one transactional
 * request. A playlist item is an id_gh_cena, so the API imports each picked
 * template as a cena first — selecting templates here therefore adds them to
 * Templates e cenas as well.
 */
import { mapActions } from 'vuex'
import GloboGraphicsTemplatePicker from './GloboGraphicsTemplatePicker.vue'

import ggDrawerA11y from '@/mixins/ggDrawerA11y'

export default {
  mixins: [ggDrawerA11y],
  name: 'GloboGraphicsCreatePlaylistDrawer',
  components: { GloboGraphicsTemplatePicker },
  props: {
    value: { type: Boolean, default: false },
    codProducao: { type: [Number, String], default: null },
  },
  data() {
    return {
      nome: '',
      selected: [],
      saving: false,
    }
  },
  computed: {
    canSave() {
      return !!(this.nome.trim() && this.codProducao && !this.saving)
    },
  },
  watch: {
    value(open) {
      if (open) {
        this.nome = ''
        this.selected = []
        this.$nextTick(() => this.$refs.picker?.reload())
      }
    },
  },
  methods: {
    ...mapActions({
      createPlaylist: 'playout/createPlaylist',
      loadSavedScenes: 'globographics/loadSavedScenes',
    }),
    close() {
      this.$emit('input', false)
    },
    onScroll(e) {
      this.$refs.picker?.maybeLoadMore(e.target)
    },
    // Creation and seeding are one transaction server-side: either the playlist
    // exists with everything the operator picked, or nothing was written.
    async save() {
      if (!this.canSave) return
      this.saving = true
      try {
        const playlist = await this.createPlaylist({
          codProducao: Number(this.codProducao),
          nome: this.nome.trim(),
          // Send the title too, or the seeded cenas land as "Vizrt Template 18712".
          templates: this.selected.map((t) => ({ templateId: t.id, dsc_nome_cena: t.title })),
        })

        // The seeded templates became cenas of the production, so the
        // "Templates e cenas" list is now stale.
        if (this.selected.length) {
          await this.loadSavedScenes({ codProducao: this.codProducao })
        }
        this.$emit('created', playlist?.id_playlist)
        this.close()
      } catch (e) {
        this.$toast?.error?.(e.response?.data?.error || 'Erro ao criar playlist')
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
// Drawer chrome comes from _gg-import-drawer.scss; the name field is specific.
.gg-create-pl__label {
  display: block;
  margin-bottom: var(--sc-space-2);
  font-size: 14px;
  font-weight: var(--sc-weight-medium);
  line-height: 16px;
  letter-spacing: 0.07px;
  color: var(--sc-gg-text-strong);
}

.gg-create-pl__required {
  margin-left: 2px;
  color: var(--sc-gg-error);
}

.gg-create-pl__name {
  width: 600px;
  max-width: 100%;
  height: 56px;
  padding: 0 var(--sc-space-3);
  background: var(--sc-gg-surface-2);
  border: 1px solid var(--sc-gg-border-100);
  border-radius: var(--sc-radius-md);
  outline: none;
  color: var(--sc-gg-text-primary);
  font-size: 16px;
  line-height: 24px;

  &::placeholder {
    color: var(--sc-gg-text-tertiary);
  }

  &:focus {
    border-color: var(--sc-gg-accent);
  }
}

.gg-create-pl__rule {
  height: 0;
  margin: var(--sc-space-6) 0;
  border: 0;
  border-top: 1px solid var(--sc-gg-border-100);
}
</style>
